import { createAsyncThunk } from '@reduxjs/toolkit'
import type { AxiosError } from 'axios'

import { ValidationErrors } from 'store/types'

import { getAxiosInstance } from 'services/api'

import { League } from 'types/app'

const api = getAxiosInstance()

export const getAllLeagues = createAsyncThunk<
  League[],
  void,
  { rejectValue: ValidationErrors }
>('league/getAllLeagues', async (_, { rejectWithValue }) => {
  try {
    console.log('Fetching leagues from /all/leagues...')
    const response = await api.get('/all/leagues')
    console.log('League response:', response.data)
    const leagues = response.data.leagues || []
    console.log('Returning leagues:', leagues.length, 'leagues')
    return leagues
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>
    console.error('League fetch error:', err)

    if (!error.response) throw err
    
    // If we get a 500 error, return empty array to keep app working
    if (error.response.status === 500) {
      console.error('Backend error fetching leagues, returning empty array')
      return []
    }

    return rejectWithValue(error.response.data)
  }
})

export const joinLeague = createAsyncThunk<
  { success: boolean },
  { leagueId: string; teamId: string; requestedTier?: string },
  { rejectValue: ValidationErrors }
>('league/joinLeague', async ({ leagueId, teamId, requestedTier }, { rejectWithValue }) => {
  try {
    const response = await api.post(`/league/${leagueId}/register`, { teamId, requestedTier })
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>
    console.error('Join league error:', err)

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

export const leaveLeague = createAsyncThunk<
  { success: boolean },
  { leagueId: string; teamId: string },
  { rejectValue: ValidationErrors }
>('league/leaveLeague', async ({ leagueId, teamId }, { rejectWithValue }) => {
  try {
    const response = await api.post(`/league/${leagueId}/unregister`, { teamId })
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>
    console.error('Leave league error:', err)

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

export const getLeagueMatches = createAsyncThunk<
  any,
  { leagueId: string; view?: string },
  { rejectValue: ValidationErrors }
>('league/getMatches', async ({ leagueId, view }, { rejectWithValue }) => {
  try {
    const params = view ? `?view=${view}` : ''
    const response = await api.get(`/league/${leagueId}/matches${params}`)
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>
    console.error('Get league matches error:', err)

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

export const getLeagueNews = createAsyncThunk<
  any,
  { leagueId: string; page?: number; category?: string },
  { rejectValue: ValidationErrors }
>('league/getNews', async ({ leagueId, page = 1, category }, { rejectWithValue }) => {
  try {
    const params = new URLSearchParams()
    if (page) params.append('page', page.toString())
    if (category) params.append('category', category)
    
    const response = await api.get(`/league/${leagueId}/news?${params}`)
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>
    console.error('Get league news error:', err)

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

export const getSingleNews = createAsyncThunk<
  any,
  { newsId: string },
  { rejectValue: ValidationErrors }
>('league/getSingleNews', async ({ newsId }, { rejectWithValue }) => {
  try {
    const response = await api.get(`/league/news/${newsId}`)
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>
    console.error('Get single news error:', err)

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

export const addNewsComment = createAsyncThunk<
  any,
  { newsId: string; comment: string },
  { rejectValue: ValidationErrors }
>('league/addNewsComment', async ({ newsId, comment }, { rejectWithValue }) => {
  try {
    const response = await api.post(`/league/news/${newsId}/comment`, { comment })
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>
    console.error('Add news comment error:', err)

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

export const toggleNewsLike = createAsyncThunk<
  any,
  { newsId: string },
  { rejectValue: ValidationErrors }
>('league/toggleNewsLike', async ({ newsId }, { rejectWithValue }) => {
  try {
    const response = await api.post(`/league/news/${newsId}/like`)
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>
    console.error('Toggle news like error:', err)

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

export const deleteLeague = createAsyncThunk<
  { success: boolean },
  { leagueId: string },
  { rejectValue: ValidationErrors }
>('league/deleteLeague', async ({ leagueId }, { rejectWithValue }) => {
  try {
    const response = await api.delete(`/admin/league/${leagueId}`)
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>
    console.error('Delete league error:', err)

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})
